import { useMemo } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { queryClient } from "App";
import {
  BaseForm,
  FormInput,
  FormMultiSelect,
  FormSelect,
  FormToggle,
} from "components/BaseForm";
import {
  FEATURE_FLAG_ACCESS_TYPE,
  SYSTEM_SETTINGS_FLAG_KEY,
} from "constants/Constants";
import { formatUserName } from "helpers/user";
import FeatureFlagService from "services/FeatureFlag";
import RoleService from "services/Role";
import UserService from "services/User";

const FLAGS_QUERY_KEY = ["feature-flags"];

const ACCESS_TYPE_OPTIONS = [
  {
    key: "everyone",
    value: FEATURE_FLAG_ACCESS_TYPE.EVERYONE,
    text: "Everyone",
  },
  { key: "custom", value: FEATURE_FLAG_ACCESS_TYPE.CUSTOM, text: "Custom" },
];

const makeSchema = isEdit =>
  z.object({
    key: isEdit ? z.string().optional() : z.string().min(1, "Key is required"),
    name: z.string().min(1, "Name is required"),
    enabled: z.boolean(),
    access_type: z.number(),
    users_access: z.array(z.number()),
    roles_access: z.array(z.number()),
  });

// The access lists only apply to the custom access type, so they render only
// when it is selected. `useFormContext` reads the live value inside BaseForm's
// FormProvider.
const AccessFields = ({ userOptions, roleOptions }) => {
  const accessType = useFormContext().watch("access_type");
  if (accessType !== FEATURE_FLAG_ACCESS_TYPE.CUSTOM) {
    return null;
  }
  return (
    <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
      <FormMultiSelect
        name="users_access"
        label="Users"
        options={userOptions}
      />
      <FormMultiSelect
        name="roles_access"
        label="Roles"
        options={roleOptions}
      />
    </div>
  );
};

const FeatureFlagForm = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const isAdmin = UserService.isAdmin();

  const {
    data: flag,
    isSuccess: flagLoaded,
    isError: flagError,
  } = useQuery({
    queryKey: ["feature-flag", id],
    queryFn: () => FeatureFlagService.get(id),
    enabled: isEdit && !!id,
    retry: false,
  });

  const { data: usersData, isFetched: usersFetched } = useQuery({
    queryKey: ["users-for-filters", "feature-flags"],
    queryFn: () => UserService.getUsersForFilters(),
    retry: false,
  });

  const { data: rolesData, isFetched: rolesFetched } = useQuery({
    queryKey: ["roles-for-filters"],
    queryFn: () => RoleService.getRoles(),
    retry: false,
  });

  const userOptions = useMemo(
    () =>
      (usersData ?? []).map(user => ({
        key: user.id,
        value: user.id,
        text: formatUserName(user.full_name || user.name, user.status),
      })),
    [usersData]
  );

  const roleOptions = useMemo(
    () =>
      (rolesData ?? []).map(role => ({
        key: role.id,
        value: role.id,
        text: role.name,
      })),
    [rolesData]
  );

  const defaultValues = useMemo(
    () => ({
      key: flag?.key ?? "",
      name: flag?.name ?? "",
      enabled: flag?.enabled ?? true,
      access_type: flag?.access_type ?? FEATURE_FLAG_ACCESS_TYPE.EVERYONE,
      users_access: flag?.users_access ?? [],
      roles_access: flag?.roles_access ?? [],
    }),
    [flag]
  );

  const handleSubmit = async values => {
    // The access lists are meaningless for the everyone type; send empty
    // lists so a switch away from custom clears any stale targeting.
    const isCustom = values.access_type === FEATURE_FLAG_ACCESS_TYPE.CUSTOM;
    const payload = {
      name: values.name,
      enabled: values.enabled,
      access_type: values.access_type,
      users_access: isCustom ? values.users_access : [],
      roles_access: isCustom ? values.roles_access : [],
    };
    if (isEdit) {
      await FeatureFlagService.update(id, payload);
    } else {
      await FeatureFlagService.create({ ...payload, key: values.key });
    }
    // Flags apply on full reload, so only the admin table query is refreshed.
    queryClient.invalidateQueries({ queryKey: FLAGS_QUERY_KEY });
    toast.success(isEdit ? "Flag updated" : "Flag created");
    navigate("/settings/feature_flags");
  };

  const handleCancel = () => navigate("/settings/feature_flags");

  if (!isAdmin) {
    return <Navigate to="/settings/feature_flags" replace />;
  }

  if (flagError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <p>{"Couldn't load this flag. Please try again."}</p>
        <Link to="/settings/feature_flags">Back to Feature Flags</Link>
      </div>
    );
  }

  const ready = (!isEdit || flagLoaded) && usersFetched && rolesFetched;

  if (!ready) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <Spinner />
      </div>
    );
  }

  // The self-referential settings flag must stay enabled, so its toggle is
  // read-only; its access lists remain editable.
  const isSettingsFlag =
    flag?.is_system && flag?.key === SYSTEM_SETTINGS_FLAG_KEY;

  const pageTitle = isEdit ? "Edit Feature Flag" : "New Feature Flag";

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-start tw:font-sans tw:text-[var(--color-text-primary)]">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          enablePageTitleActions
          pageTitle={pageTitle}
          pageTitleActions={
            <FormToggle
              name="enabled"
              label="Enabled"
              disabled={isSettingsFlag}
            />
          }
          backLink={{
            to: "/settings/feature_flags",
            label: "Back to Feature Flags",
          }}
          schema={makeSchema(isEdit)}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitButtonText="Save"
        >
          <Card>
            <CardHeader>
              <CardTitle>Flag</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                <FormInput
                  name="key"
                  label="Key"
                  required={!isEdit}
                  disabled={isEdit}
                />
                <FormInput name="name" label="Name" required />
                <FormSelect
                  name="access_type"
                  label="Access Type"
                  options={ACCESS_TYPE_OPTIONS}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Access</CardTitle>
            </CardHeader>
            <CardContent>
              <AccessFields
                userOptions={userOptions}
                roleOptions={roleOptions}
              />
            </CardContent>
          </Card>
        </BaseForm>
      </div>
    </div>
  );
};

export default FeatureFlagForm;
